import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 border-b border-background-200/70 py-2'
          : 'bg-transparent py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-14">
        <a
          href="/"
          className="text-foreground-900 font-heading text-xl font-semibold tracking-wide whitespace-nowrap hover:text-accent-500 transition-colors duration-200"
        >
          yoybuy.hair
        </a>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-4 py-2.5 border border-background-300/60 rounded-lg text-sm text-foreground-700 font-label font-medium bg-background-50 hover:border-accent-300 hover:text-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              aria-expanded={isOpen}
            >
              <span>{t('categorySelect')}</span>
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>

            {isOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200/70 rounded-lg py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-600 transition-colors duration-150 whitespace-nowrap"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span>{cat.nameRu}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-label font-medium hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('yoybuySheet')}
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-label font-medium hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('buyNow')}
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          aria-label={mobileMenuOpen ? t('navClose') : t('navOpen')}
        >
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <div className="mb-3">
            <p className="text-xs text-foreground-500 font-label font-medium mb-2 uppercase tracking-wide">{t('allCategories')}</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-600 transition-colors duration-150 whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  <span>{cat.nameRu}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-label font-medium text-center hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('yoybuySheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-label font-medium text-center hover:bg-accent-600 transition-all duration-200 whitespace-nowrap"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('buyNow')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}